import React, { Component } from 'react'
import { View, Platform } from 'react-native'
import Slider from '../../Atoms/VerticalSlider'
import styled from 'styled-components/native'

import { Text } from '../../Atoms/Typography'
import Button from '../../Atoms/Button'
import TranslatedText from '../../Atoms/TranslatedText'
import StackedBarChart from './StackedBarChart'
import PageHeader from '../../Molecules/PageHeader'
import ValueChangeText from '../../Molecules/ValueChangeText'
import { calculateCapitalMovement, calculateForecastValues, calculateRiskWeightedAssets, calculateForecastDeductions, calculateTotalOpeningBalance, calculateGrowthPerBusiness } from './calculations'
import { thousandSeparator, percentage } from '../../../lib/formatters'
import { translate } from '../../../services/translate'
import theme from '../../../styles/theme'

const sliderHeight = 400
const valueBoxWidth = 90

const Container = styled(View)`
  flex: 1;
  flex-direction: column;
`

const Content = styled(View)`
  flex-direction: row;
  padding: 0 ${theme.gutterUnit}px;
`

const ChartContainer = styled(View)`
  flex: 1;
  flex-direction: column;
  padding-top: ${theme.gutterUnit}px;
`

const SlidersContainer = styled(View)`
  flex: 1;
`

const BarChartContainer = styled(View)`
  height: 380px;
  align-items: center;
  justify-content: flex-end;
  margin-top: ${theme.gutterUnit}px;
  padding-top: ${theme.gutterUnit}px;
  border-color: ${theme.colours.bordergrey};
  border-width: 1px;
  background-color: ${theme.colours.white};
`

const CalculationsContainer = styled(View)`
  height: 135px;
  padding-left: ${6 * theme.gutterUnit}px;
  padding-right: ${6 * theme.gutterUnit}px;
  padding-top: ${1.5 * theme.gutterUnit}px;
  padding-bottom: ${theme.gutterUnit}px;
  border-color: grey;
  border-width: 1px;
  background-color: ${theme.colours.white};
`

const CalculationRow = styled(View)`
  flex-direction: row;
`

const Calculation = styled(View)`
  width: 33.33%;
  align-items: center;
  justify-content: space-between;
`

const SlidersHeadersContainer = styled(View)`
  flex-direction: row;
`

const VerticalSliderColumn = styled(View)`
  width: 25%;
  align-items: center;
  justify-content: flex-start;
  padding-top: ${0.5 * theme.gutterUnit}px;
`

const SliderValueContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`

const SliderValue = styled(View)`
  width: ${valueBoxWidth}px;
  padding-top: ${theme.gutterUnit}px;
  padding-bottom: ${theme.gutterUnit}px;
  border-width: 1px;
  border-color: ${props => props.mandatory ? theme.colours.blue : theme.colours.bordergrey};
  background-color: ${props => props.mandatory ? theme.colours.blue : theme.colours.white};
  border-radius: 5px;
`

const SliderHeader = styled(View)`

`

const Slider1Container = styled(View)`
  position: absolute;
  transform: rotate(${Platform.select({ web: '90deg', default: '-90deg' })});
  width: ${sliderHeight}px;
  top: 230px;
`

const ShareCapitalContainer = styled(View)`
  margin-top: ${(3 * theme.gutterUnit) + 5}px;
  margin-left: ${theme.gutterUnit}px;
  padding: ${theme.gutterUnit}px;
  border-color: grey;
  border-width: 1px;
  background-color: ${theme.colours.white};
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const XAxisLabels = styled(View)`
  flex-direction: row;
  padding: ${theme.gutterUnit}px;
  justify-content: space-between;
  align-items: center;
`

const XAxisLabel = styled(View)`
  width: 50%;
  justify-content: center;
  align-items: center;
`

const ButtonsContainer = styled(View)`
  margin-top: ${2 * theme.gutterUnit}px;
  flex-direction: column;
  height: ${sliderHeight}px;
  justify-content: space-between;
`

const ButtonContainer = styled(View)`
  width: ${valueBoxWidth}px;
`

const ZeroMark = styled(View)`
  height: 2px;
  width: 20px;
  position: absolute;
  top: 250px;
  background-color: ${theme.colours.grey};
`

const formatValue = (val, addPlusSign) => {
  if (val === 0) {
    return '-'
  }
  if (val < 0) {
    return `(${thousandSeparator(val * -1)})`
  }
  return `${addPlusSign ? '+' : ''}${thousandSeparator(val)}`
}

const formatPercentage = (val, dp, hidePercentage) => {
  if (val === 0) {
    return '-'
  }
  return percentage(val, dp, hidePercentage)
}

const barChartColours = [{
  colour: '#d286ba',
  key: 'cash'
}, {
  colour: theme.colours.orange,
  key: 'allOtherAssets'
}, {
  colour: theme.colours.blue,
  key: 'shareholdersFunds'
}, {
  colour: theme.colours.aqua,
  key: 'deposits'
}, {
  colour: '#93d8fc',
  key: 'wholesaleFunding'
}]

const calculateWsfValue = (previousVal, previousTotal, movementsAndRepayments, defaultValue) => {
  if (previousTotal * movementsAndRepayments === 0) {
    return defaultValue
  }
  return parseInt(((previousVal / previousTotal) * movementsAndRepayments).toFixed(0))
}

export default class TreasuryBalanceSheet extends Component {
  state = {
    barChartWidth: null
  }

  shouldComponentUpdate (nextProps, nextState) {
    if (!nextProps.active) {
      return false
    }
    return true
  }

  handleLayout = event => {
    const { width } = event.nativeEvent.layout
    if (this.state.barChartWidth === width) return // layout was already called

    this.setState({
      barChartWidth: width
    })
  }

  onAssetGrowthChange = value => {
    const { treasury: { openingBalances }, decisions } = this.props
    const totalOpeningValue = calculateTotalOpeningBalance(openingBalances)
    const forecastValues = calculateForecastValues(false, openingBalances, decisions)
    const totalGrowth = totalOpeningValue * (value / 10000)

    this.props.onChangeValue({
      forceRerender: new Date().valueOf(),
      allOtherAssetsPercentage: value,
      ...calculateGrowthPerBusiness(totalOpeningValue, totalGrowth, forecastValues, openingBalances)
    })
  }

  onWsfChange = value => {
    const { treasury: { openingBalances }, decisions: { wsf1, wsf2, wsf3, wsf4, wsf5 } } = this.props
    const wsfTotal = wsf1 + wsf2 + wsf3 + wsf4 + wsf5
    const wsfMovementAndRepayments = value + Math.abs(openingBalances.wsfRepayments)

    this.props.onChangeValue({
      forceRerender: new Date().valueOf(),
      wsfChange: value,
      wsf1: calculateWsfValue(wsf1, wsfTotal, wsfMovementAndRepayments, 0),
      wsf2: calculateWsfValue(wsf2, wsfTotal, wsfMovementAndRepayments, 0),
      wsf3: calculateWsfValue(wsf3, wsfTotal, wsfMovementAndRepayments, 0),
      wsf4: calculateWsfValue(wsf4, wsfTotal, wsfMovementAndRepayments, 0),
      wsf5: calculateWsfValue(wsf5, wsfTotal, wsfMovementAndRepayments, wsfMovementAndRepayments)
    })
  }

  render () {
    const { treasury: { openingBalances, wsfSliderMin, capitalSliderMin, capitalSliderMax, wsfMethod, capitalMethod, capitalBands, rates, capitalBuffer }, decisions, onChangeValue, enableChanges } = this.props
    const showWholesaleFundingSlider = wsfMethod !== 3
    const showCapitalRatioSlider = capitalMethod === 1
    const showCreditRatingButtons = capitalMethod === 2
    const allOtherAssetsMovements = (openingBalances.allOtherAssets * (decisions.allOtherAssetsPercentage / 100 / 100))
    const allOtherAssetsForecast = parseInt(openingBalances.allOtherAssets + allOtherAssetsMovements)
    const depositsMovement = (openingBalances.deposits * (decisions.depositGrowthChangePercentage / 100 / 100))
    const depositsForecast = parseInt(openingBalances.deposits + depositsMovement)
    const wholesaleFundingForecast = parseInt(openingBalances.wholesaleFunding + decisions.wsfChange)

    const assetForecasts = calculateForecastValues(false, openingBalances, decisions)

    const { totalRiskWeightedAsset } = calculateRiskWeightedAssets(assetForecasts, decisions)

    const { totalCapitalDeductions } = calculateForecastDeductions(assetForecasts, decisions)
    const targetShareCapitalMovement = calculateCapitalMovement(totalRiskWeightedAsset, -1 * totalCapitalDeductions, decisions, openingBalances)

    const shareholdersFundsForecast = openingBalances.shareholdersFunds + targetShareCapitalMovement
    const cashForecast = Math.max(0, parseInt((openingBalances.cash + depositsMovement + decisions.wsfChange + targetShareCapitalMovement - (allOtherAssetsMovements))))

    const currentEirCash = (rates.cashYield / 100 / 100) * openingBalances.cash
    const forecastEirCash = (rates.cashYield / 100 / 100) * cashForecast
    const currentEirAsset = (rates.assetYield / 100 / 100) * openingBalances.allOtherAssets
    const forecastEirAsset = (rates.assetYield / 100 / 100) * allOtherAssetsForecast
    const currentEirAssetTotal = currentEirCash + currentEirAsset
    const forecastEirAssetTotal = forecastEirCash + forecastEirAsset
    const currentEirAssetPercentage = (currentEirAssetTotal / (openingBalances.cash + openingBalances.allOtherAssets)) * 100 * 100
    const forecastEirAssetPercentage = (forecastEirAssetTotal / (cashForecast + allOtherAssetsForecast)) * 100 * 100

    const currentEirDeposits = (rates.costOfDeposits / 100 / 100) * openingBalances.deposits
    const forecastEirDeposits = (rates.costOfDeposits / 100 / 100) * depositsForecast
    const currentEirWholesale = (rates.costOfWSF / 100 / 100) * openingBalances.wholesaleFunding
    const forecastEirWholesale = (rates.costOfWSF / 100 / 100) * wholesaleFundingForecast
    const currentEirCostTotal = currentEirDeposits + currentEirWholesale
    const forecastEirCostTotal = forecastEirDeposits + forecastEirWholesale
    const currentEirCostPercentage = (currentEirCostTotal / (openingBalances.deposits + openingBalances.wholesaleFunding)) * 100 * 100
    const forecastEirCostPercentage = (forecastEirCostTotal / (depositsForecast + wholesaleFundingForecast)) * 100 * 100

    const currentEirMargin = currentEirAssetPercentage - currentEirCostPercentage
    const forecastEirMargin = forecastEirAssetPercentage - forecastEirCostPercentage
    const marginChange = forecastEirMargin - currentEirMargin

    const data = [{
      label: 'ASSETS',
      allOtherAssets: allOtherAssetsForecast,
      cash: cashForecast,
      wholesaleFunding: 0,
      deposits: 0,
      shareholdersFunds: 0
    }, {
      label: 'FUNDING',
      allOtherAssets: 0,
      cash: 0,
      wholesaleFunding: wholesaleFundingForecast,
      deposits: depositsForecast,
      shareholdersFunds: parseInt(shareholdersFundsForecast.toFixed(0))
    }]

    const yaxisInterval = 25000
    const actualMaxValue = Math.max(allOtherAssetsForecast + cashForecast, wholesaleFundingForecast, depositsForecast, shareholdersFundsForecast)
    const maxValue = Math.ceil(Math.abs(actualMaxValue) / yaxisInterval) * yaxisInterval

    return (
      <Container>
        <PageHeader text={translate('treasury.screen1.screenHeader', 'TREASURY BALANCE SHEET').toUpperCase()} />
        <Content>
          <ChartContainer>
            <TranslatedText center proximaBold size='xl' uppercase value='treasury.screen1.chartHeader' default='FORECAST ASSETS & FUNDING' />
            <BarChartContainer onLayout={this.handleLayout}>
              {this.state.barChartWidth && (
                <StackedBarChart
                  colours={barChartColours}
                  data={data}
                  height={380}
                  width={this.state.barChartWidth}
                  numberOfTicks={maxValue / yaxisInterval + 1}
                  maxValue={maxValue}
                />)}
            </BarChartContainer>
            <XAxisLabels>
              <XAxisLabel>
                <TranslatedText bold darkgrey center overrides={{ marginRight: 0.2 * theme.gutterUnit }} uppercase value='treasury.screen1.chartBar1Label' default='ASSETS' />
              </XAxisLabel>
              <XAxisLabel>
                <TranslatedText bold darkgrey center overrides={{ marginLeft: 0.2 * theme.gutterUnit }} uppercase value='treasury.screen1.chartBar2Label' default='FUNDING' />
              </XAxisLabel>
            </XAxisLabels>
            <CalculationsContainer>
              <CalculationRow>
                <Calculation>
                  <TranslatedText bold darkgrey size='s' center value='treasury.screen1.impact1Label' default='Return on Assets' />
                  <TranslatedText darkgrey center size='xs' value='treasury.screen1.impact1SubLabel' default='(Illustrative Only)' />
                </Calculation>
                <Calculation>
                  <TranslatedText bold darkgrey size='s' center value='treasury.screen1.impact2Label' default='Margin' />
                </Calculation>
                <Calculation>
                  <TranslatedText bold darkgrey size='s' center value='treasury.screen1.impact3Label' default='Funding Cost' />
                </Calculation>
              </CalculationRow>
              <CalculationRow style={{ marginTop: theme.gutterUnit }}>
                <Calculation>
                  <Text medium size='l'>{formatPercentage(forecastEirAssetPercentage)}</Text>
                </Calculation>
                <Calculation>
                  <Text medium size='l'>{formatPercentage(forecastEirMargin)}</Text>
                  <ValueChangeText value={marginChange} dp={2} changeUnit='%' dashIfZero bold />
                </Calculation>
                <Calculation>
                  <Text medium size='l' colour={theme.colours.red}>({formatPercentage(forecastEirCostPercentage)})</Text>
                </Calculation>
              </CalculationRow>
            </CalculationsContainer>
          </ChartContainer>
          <SlidersContainer>
            <SlidersHeadersContainer>
              <VerticalSliderColumn>
                <SliderHeader>
                  <TranslatedText center size='s' value='treasury.screen1.slider1Label' default={'Asset\nGrowth'} />
                </SliderHeader>
                <ZeroMark />
                <Slider1Container>
                  <Slider
                    key='allOtherAssetsSlider'
                    value={decisions.allOtherAssetsPercentage}
                    onValueChange={value => this.onAssetGrowthChange(value)}
                    minimumValue={-5000}
                    maximumValue={5000}
                    minimumTrackTintColor={enableChanges ? theme.colours.orange : theme.colours.grey}
                    maximumTrackTintColor={theme.colours.grey}
                    thumbTintColor={enableChanges ? theme.colours.orange : theme.colours.grey}
                    thumbTouchSize={{ width: 80, height: 80 }}
                    step={100}
                    orientation='vertical'
                    inverted
                    disabled={!enableChanges}
                  />
                </Slider1Container>
                <ButtonsContainer />
              </VerticalSliderColumn>
              <VerticalSliderColumn>
                <SliderHeader>
                  <TranslatedText center size='s' value='treasury.screen1.slider2Label' default={'Deposit\nGrowth'} />
                </SliderHeader>
                <ZeroMark />
                <Slider1Container>
                  <Slider
                    key='depositsSlider'
                    value={decisions.depositGrowthChangePercentage}
                    onValueChange={value => onChangeValue({ depositGrowthChangePercentage: value })}
                    minimumValue={-5000}
                    maximumValue={5000}
                    minimumTrackTintColor={enableChanges ? theme.colours.orange : theme.colours.grey}
                    maximumTrackTintColor={theme.colours.grey}
                    thumbTintColor={enableChanges ? theme.colours.orange : theme.colours.grey}
                    thumbTouchSize={{ width: 80, height: 80 }}
                    step={100}
                    orientation='vertical'
                    inverted
                    disabled={!enableChanges}
                  />
                </Slider1Container>
                <ButtonsContainer />
              </VerticalSliderColumn>
              {showWholesaleFundingSlider && (
                <VerticalSliderColumn>
                  <SliderHeader>
                    <TranslatedText center size='s' value='treasury.screen1.slider3Label' default={'Wholesale\nFunding Change'} />
                  </SliderHeader>
                  <Slider1Container>
                    <Slider
                      key='wholesaleFundingSlider'
                      value={decisions.wsfChange}
                      onValueChange={value => this.onWsfChange(value)}
                      minimumValue={wsfSliderMin}
                      maximumValue={100000 - Math.abs(openingBalances.wsfRepayments)}
                      minimumTrackTintColor={enableChanges ? theme.colours.blue : theme.colours.grey}
                      maximumTrackTintColor={theme.colours.grey}
                      thumbTintColor={enableChanges ? theme.colours.blue : theme.colours.grey}
                      thumbTouchSize={{ width: 80, height: 80 }}
                      step={1000}
                      orientation='vertical'
                      inverted
                      disabled={!enableChanges}
                    />
                  </Slider1Container>
                  <ButtonsContainer />
                </VerticalSliderColumn>
              )}
              {showCreditRatingButtons && (
                <VerticalSliderColumn>
                  <SliderHeader>
                    <TranslatedText center size='s' value='treasury.screen1.slider5Label' default={'Credit\nRating'} />
                  </SliderHeader>
                  <ButtonsContainer>
                    {capitalBands.map((cb) => {
                      const valueIsInBand = cb.bandName === decisions.desiredCreditRating
                      return (
                        <ButtonContainer key={cb.bandName}>
                          <Button
                            disabled={!enableChanges}
                            rounded
                            noborder
                            colour={enableChanges || valueIsInBand ? theme.colours.blue : theme.colours.grey}
                            styles={[{ opacity: (valueIsInBand && enableChanges) ? 1 : 0.5 }]}
                            onPress={() => onChangeValue({
                              capitalRatioChange: cb.bandValue + capitalBuffer,
                              desiredCreditRating: cb.bandName
                            })}
                          >
                            <Text white>{cb.bandName}</Text>
                          </Button>
                        </ButtonContainer>
                      )
                    })}
                  </ButtonsContainer>
                </VerticalSliderColumn>
              )}
              {showCapitalRatioSlider && (
                <VerticalSliderColumn>
                  <SliderHeader>
                    <TranslatedText center size='s' value='treasury.screen1.slider4Label' default={'Estimated\nCapital Ratio'} />
                  </SliderHeader>
                  <Slider1Container>
                    <Slider
                      key='capitalRatioSlider'
                      value={decisions.capitalRatioChange}
                      onValueChange={value => onChangeValue({ capitalRatioChange: value })}
                      minimumValue={capitalSliderMin}
                      maximumValue={capitalSliderMax}
                      minimumTrackTintColor={enableChanges ? theme.colours.blue : theme.colours.grey}
                      maximumTrackTintColor={theme.colours.grey}
                      thumbTintColor={enableChanges ? theme.colours.blue : theme.colours.grey}
                      thumbTouchSize={{ width: 80, height: 80 }}
                      step={1}
                      orientation='vertical'
                      inverted
                      disabled={!enableChanges}
                    />
                  </Slider1Container>
                  <ButtonsContainer />
                </VerticalSliderColumn>
              )}
            </SlidersHeadersContainer>
            <View style={{
              marginTop: theme.gutterUnit
            }}
            >
              <SlidersHeadersContainer>
                <VerticalSliderColumn style={{ justifyContent: 'flex-start' }}>
                  <SliderValueContainer>
                    <SliderValue>
                      <Text center>{formatPercentage(decisions.allOtherAssetsPercentage, 0, true)}</Text>
                    </SliderValue>
                    <TranslatedText
                      overrides={{
                        position: 'absolute',
                        left: valueBoxWidth + 5
                      }} darkgrey
                      value='general.percentage'
                      default='%'
                    />
                  </SliderValueContainer>
                </VerticalSliderColumn>
                <VerticalSliderColumn style={{ justifyContent: 'flex-start' }}>
                  <SliderValueContainer>
                    <SliderValue>
                      <Text center>{formatPercentage(decisions.depositGrowthChangePercentage, 0, true)}</Text>
                    </SliderValue>
                    <TranslatedText
                      overrides={{
                        position: 'absolute',
                        left: valueBoxWidth + 5
                      }} darkgrey
                      value='general.percentage'
                      default='%'
                    />
                  </SliderValueContainer>
                </VerticalSliderColumn>
                {showWholesaleFundingSlider && (
                  <VerticalSliderColumn style={{ justifyContent: 'flex-start' }}>
                    <SliderValueContainer>
                      <SliderValue mandatory>
                        <Text white center>{formatValue(decisions.wsfChange, true)}</Text>
                      </SliderValue>
                      <TranslatedText
                        overrides={{
                          position: 'absolute',
                          left: valueBoxWidth + 5
                        }} darkgrey
                        value='general.denominationShort'
                        default='m'
                      />
                    </SliderValueContainer>
                  </VerticalSliderColumn>
                )}
                {showCreditRatingButtons && (
                  <VerticalSliderColumn style={{ justifyContent: 'flex-start' }}>

                    <SliderValueContainer>
                      <SliderValue mandatory>
                        <Text white center>{formatValue(targetShareCapitalMovement.toFixed(0))}</Text>
                      </SliderValue>
                      <Text
                        overrides={{
                          position: 'absolute',
                          left: valueBoxWidth + 5
                        }} darkgrey
                        value='general.denominationShort'
                        default='m'
                      />
                    </SliderValueContainer>
                    <TranslatedText
                      darkgrey size='s' center overrides={{
                        marginTop: theme.gutterUnit
                      }}
                      value='treasury.screen1.additionalCapital'
                      default={'Additional\nCapital'}
                    />
                  </VerticalSliderColumn>
                )}
                {showCapitalRatioSlider && (
                  <VerticalSliderColumn style={{ justifyContent: 'flex-start' }}>
                    <SliderValueContainer>
                      <SliderValue mandatory>
                        <Text white center>{formatPercentage(decisions.capitalRatioChange, 2, true)}</Text>
                      </SliderValue>
                      <TranslatedText
                        overrides={{
                          position: 'absolute',
                          left: valueBoxWidth + 5
                        }} darkgrey
                        value='general.percentage'
                        default='%'
                      />
                    </SliderValueContainer>
                  </VerticalSliderColumn>
                )}
              </SlidersHeadersContainer>
              {showCapitalRatioSlider && (
                <ShareCapitalContainer>
                  <TranslatedText value='treasury.screen1.capitalMovement' default='Estimated Share Capital Movement' />
                  <Text bold size='l' medium>{formatValue(targetShareCapitalMovement.toFixed(0))}</Text>
                </ShareCapitalContainer>
              )}
            </View>
          </SlidersContainer>
        </Content>
      </Container>
    )
  }
}
